import * as THREE from 'three';
import { useRef, useEffect } from 'react';

function UpperEnd({ convRatio, color, drawingBaseDimensions }) {
  const width = convRatio * drawingBaseDimensions.belt_width;
  const windThickness = drawingBaseDimensions.winding_thickness;
  const height = convRatio * windThickness;
  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;
  const inclination =
    THREE.MathUtils.degToRad(drawingBaseDimensions.slope_angle_center) *
    reverseConfig;
  const inclination_db = inclination / 2;

  const centralDiameter = drawingBaseDimensions.central_diam;

  const UEAngular = drawingBaseDimensions.ue_angular;
  const depth_angular = (convRatio * UEAngular) / Math.cos(inclination);
  const depth_db = drawingBaseDimensions.ue_double_bend_value
    ? (convRatio * drawingBaseDimensions.ue_double_bend_value) /
      Math.cos(inclination_db)
    : 0;
  const depth_flat = drawingBaseDimensions.u2_value
    ? convRatio * drawingBaseDimensions.u2_value
    : 0;

  const heightDiffUE =
    (Math.tan(inclination) * reverseConfig * UEAngular +
      Math.tan(inclination_db) *
        drawingBaseDimensions.ue_double_bend_value *
        reverseConfig) *
    convRatio;
  const pos_y_ang =
    convRatio * (drawingBaseDimensions.h3 - windThickness / 2) +
    (Math.sin(inclination) * reverseConfig * depth_angular) / 2 -
    heightDiffUE;
  const pos_y_db =
    pos_y_ang +
    (Math.sin(inclination) * depth_angular * reverseConfig) / 2 +
    (Math.sin(inclination_db) * depth_db * reverseConfig) / 2;
  const pos_y_flat =
    pos_y_db + (Math.sin(inclination_db) * reverseConfig * depth_db) / 2;
  const configAng = THREE.MathUtils.degToRad(
    reverseConfig === 1
      ? drawingBaseDimensions.configuration_angle
      : 360 - drawingBaseDimensions.configuration_angle
  );

  const guideHeightOutside =
    convRatio * (drawingBaseDimensions.outside_guide_height + windThickness);
  const guideHeightInside =
    convRatio * (drawingBaseDimensions.inside_guide_height + windThickness);
  const guideThicknessOutside =
    convRatio * drawingBaseDimensions.default_guide_thickness;
  const guideThicknessInside =
    convRatio * drawingBaseDimensions.default_guide_thickness;

  const isOutsideGuide =
    drawingBaseDimensions.outside_guide_type === 'Stainless steel' ||
    drawingBaseDimensions.offset_ue_outside > 0;
  const isInsideGuide =
    drawingBaseDimensions.inside_guide_type === 'Stainless steel' ||
    drawingBaseDimensions.offset_ue_inside > 0;

  const posRadiusGuideOutside =
    convRatio *
      (drawingBaseDimensions.footprint_diameter / 2 +
        drawingBaseDimensions.offset_ue_outside) +
    guideThicknessOutside / 2;

  const posRadiusGuideInside =
    convRatio * drawingBaseDimensions.column_centre_to_inside_ue -
    guideThicknessInside / 2;

  const meshRefAng = useRef();
  const meshRefDb = useRef();

  useEffect(() => {
    if (meshRefAng.current) {
      // Set the rotation order to 'YXZ' to ensure Y is applied first, then X
      meshRefAng.current.rotation.order = 'YXZ';
      meshRefAng.current.rotation.y = Math.PI * 2 - configAng;
      meshRefAng.current.rotation.x = -inclination;
    }
  }, [inclination, configAng]);

  useEffect(() => {
    if (meshRefDb.current) {
      // Set the rotation order to 'YXZ' to ensure Y is applied first, then X
      meshRefDb.current.rotation.order = 'YXZ';
      meshRefDb.current.rotation.y = Math.PI * 2 - configAng;
      meshRefDb.current.rotation.x = -inclination_db;
    }
  }, [inclination_db, configAng]);

  const materialProps = {
    color: color,
    roughness: 0.8,
    metalness: 0.5,
    side: THREE.DoubleSide,
  };

  const materials = [
    new THREE.MeshStandardMaterial(materialProps), // right
    new THREE.MeshStandardMaterial(materialProps), // left
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // top
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // bottom
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // front
    new THREE.MeshStandardMaterial({ ...materialProps, color: 'black' }), // back
  ];

  return (
    <>
      {depth_angular !== 0 && (
        <>
          <mesh
            ref={meshRefAng}
            position={[
              Math.cos(configAng) * convRatio * (centralDiameter / 2) +
                Math.sin(Math.PI * 2 - configAng) *
                  ((convRatio * UEAngular) / 2) *
                  reverseConfig,
              pos_y_ang,
              Math.sin(configAng) * convRatio * (centralDiameter / 2) +
                Math.cos(Math.PI * 2 - configAng) *
                  ((convRatio * UEAngular) / 2) *
                  reverseConfig,
            ]}
          >
            <boxGeometry args={[width, height, depth_angular]} />
            {materials.map((material, index) => (
              <primitive
                key={index}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {isOutsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideOutside +
                  Math.sin(Math.PI * 2 - configAng) *
                    ((convRatio * UEAngular) / 2) *
                    reverseConfig,
                pos_y_ang +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideOutside +
                  Math.cos(Math.PI * 2 - configAng) *
                    ((convRatio * UEAngular) / 2) *
                    reverseConfig,
              ]}
              rotation={[-inclination, Math.PI * 2 - configAng, 0]}
              rotation-order="YXZ"
            >
              <boxGeometry
                args={[
                  guideThicknessOutside,
                  guideHeightOutside,
                  depth_angular,
                ]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideInside +
                  Math.sin(Math.PI * 2 - configAng) *
                    ((convRatio * UEAngular) / 2) *
                    reverseConfig,
                pos_y_ang +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideInside +
                  Math.cos(Math.PI * 2 - configAng) *
                    ((convRatio * UEAngular) / 2) *
                    reverseConfig,
              ]}
              rotation={[-inclination, Math.PI * 2 - configAng, 0]}
              rotation-order="YXZ"
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_angular]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
      {depth_db !== 0 && (
        <>
          <mesh
            ref={meshRefDb}
            position={[
              Math.cos(configAng) * convRatio * (centralDiameter / 2) +
                Math.sin(Math.PI * 2 - configAng) *
                  (convRatio * UEAngular +
                    (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                      2) *
                  reverseConfig,
              pos_y_db,
              Math.sin(configAng) * convRatio * (centralDiameter / 2) +
                Math.cos(Math.PI * 2 - configAng) *
                  (convRatio * UEAngular +
                    (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                      2) *
                  reverseConfig,
            ]}
          >
            <boxGeometry args={[width, height, depth_db]} />
            {materials.map((material, index) => (
              <primitive
                key={index + 6}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {isOutsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideOutside +
                  Math.sin(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                        2) *
                    reverseConfig,
                pos_y_db +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideOutside +
                  Math.cos(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                        2) *
                    reverseConfig,
              ]}
              rotation={[-inclination_db, Math.PI * 2 - configAng, 0]}
              rotation-order="YXZ"
            >
              <boxGeometry
                args={[guideThicknessOutside, guideHeightOutside, depth_db]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideInside +
                  Math.sin(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                        2) *
                    reverseConfig,
                pos_y_db +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideInside +
                  Math.cos(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      (convRatio * drawingBaseDimensions.ue_double_bend_value) /
                        2) *
                    reverseConfig,
              ]}
              rotation={[-inclination_db, Math.PI * 2 - configAng, 0]}
              rotation-order="YXZ"
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_db]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
      {depth_flat !== 0 && (
        <>
          <mesh
            position={[
              Math.cos(configAng) * convRatio * (centralDiameter / 2) +
                Math.sin(Math.PI * 2 - configAng) *
                  (convRatio * UEAngular +
                    convRatio * drawingBaseDimensions.ue_double_bend_value +
                    depth_flat / 2) *
                  reverseConfig,
              pos_y_flat,
              Math.sin(configAng) * convRatio * (centralDiameter / 2) +
                Math.cos(Math.PI * 2 - configAng) *
                  (convRatio * UEAngular +
                    convRatio * drawingBaseDimensions.ue_double_bend_value +
                    depth_flat / 2) *
                  reverseConfig,
            ]}
            rotation={[0, Math.PI * 2 - configAng, 0]}
          >
            <boxGeometry args={[width, height, depth_flat]} />
            {materials.map((material, index) => (
              <primitive
                key={index + 12}
                attach={`material-${index}`}
                object={material}
              />
            ))}
          </mesh>
          {materials.map((material, index) => (
            <primitive
              key={index + 12}
              attach={`material-${index}`}
              object={material}
            />
          ))}
          {isOutsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideOutside +
                  Math.sin(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      convRatio * drawingBaseDimensions.ue_double_bend_value +
                      depth_flat / 2) *
                    reverseConfig,
                pos_y_flat +
                  guideHeightOutside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideOutside +
                  Math.cos(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      convRatio * drawingBaseDimensions.ue_double_bend_value +
                      depth_flat / 2) *
                    reverseConfig,
              ]}
              rotation={[0, Math.PI * 2 - configAng, 0]}
            >
              <boxGeometry
                args={[guideThicknessOutside, guideHeightOutside, depth_flat]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
          {isInsideGuide && (
            <mesh
              position={[
                Math.cos(configAng) * posRadiusGuideInside +
                  Math.sin(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      convRatio * drawingBaseDimensions.ue_double_bend_value +
                      depth_flat / 2) *
                    reverseConfig,
                pos_y_flat +
                  guideHeightInside / 2 -
                  (windThickness / 2) * convRatio,
                Math.sin(configAng) * posRadiusGuideInside +
                  Math.cos(Math.PI * 2 - configAng) *
                    (convRatio * UEAngular +
                      convRatio * drawingBaseDimensions.ue_double_bend_value +
                      depth_flat / 2) *
                    reverseConfig,
              ]}
              rotation={[0, Math.PI * 2 - configAng, 0]}
            >
              <boxGeometry
                args={[guideThicknessInside, guideHeightInside, depth_flat]}
              />
              <meshStandardMaterial
                color="silver"
                roughness={0.8}
                metalness={0.5}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}
        </>
      )}
    </>
  );
}

export default UpperEnd;
